import { Box, Card, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import React, { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { Business_Profile_Enum, Order_Status_Enum, Payment_Method_Enum, TaskAction } from '@kheops/graphql';
import OrderTertiaryActionBg from '../../assets/images/order_tertiary_action_bg.png';
import { orderAtom } from '../state/state';
import { currentContextAtom } from '../../state';
import OrderDenyDeliveryButton from './actions/OrderDenyDeliveryButton';
import OrderBuyerReceptionButton from './actions/OrderBuyerReceptionButton';
import OrderSupplierConfirmPaymentButton from './actions/OrderSupplierConfirmPaymentButton';
import OrderSupplierDenyPaymentButton from './actions/OrderPaymentDisputeButton';

export default function OrderTertiaryAction(): React.JSX.Element {
  const { t } = useTranslation(['order']);
  const order = useAtomValue(orderAtom);
  const { realm } = useAtomValue(currentContextAtom);

  const buyerCanReceiveOrder = realm === Business_Profile_Enum.Buyer && (order.status === Order_Status_Enum.OrderToBeDeliveredBySupplier || order.status === Order_Status_Enum.OrderToBePreparedBySupplier);
  const buyerCanDenyOrderDelivery = realm === Business_Profile_Enum.Buyer && order.status === Order_Status_Enum.OrderToBeReceivedByBuyer;
  const supplierCanConfirmPayment = realm === Business_Profile_Enum.Supplier && order.status === Order_Status_Enum.OrderToBePaidByBuyer && order.payment_method === Payment_Method_Enum.NoPayment;
  const supplierCanContestPayment = realm === Business_Profile_Enum.Supplier && order.status === Order_Status_Enum.OrderClosed && order.payment_method === Payment_Method_Enum.NoPayment;

  const wording = useMemo(() => {
    switch (true) {
      case buyerCanReceiveOrder:
        return {
          title: t('order:order_already_delivered'),
          description: t('order:order_already_delivered_description'),
        };
      case buyerCanDenyOrderDelivery:
        return {
          title: t('order:order_not_delivered'),
          description: t('order:order_not_delivered_description'),
        };
      case supplierCanConfirmPayment:
        return {
          title: t('order:payment_already_done'),
          description: t('order:payment_already_done_description'),
        };
      case supplierCanContestPayment:
        return {
          title: t('order:you_have_not_being_paid'),
          description: t('order:you_have_not_being_paid_description'),
        };
      default:
    }
  }, [buyerCanDenyOrderDelivery, buyerCanReceiveOrder, supplierCanConfirmPayment]);

  if (!(buyerCanReceiveOrder || buyerCanDenyOrderDelivery || supplierCanConfirmPayment || supplierCanContestPayment)) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  }

  return (
    <Card
      sx={{
        backgroundImage: `url(${OrderTertiaryActionBg})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: (theme) => theme.palette.tertiaryContainer.main,
        color: (theme) => theme.palette.onTertiaryContainer.main,
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        gap: 1.5,
        alignItems: { xs: 'flex-start', lg: 'center' },
        justifyContent: { xs: 'flex-start', lg: 'space-between' },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="titleMedium">
          {wording?.title}
        </Typography>
        <Typography variant="bodySmall">
          {wording?.description}
        </Typography>
      </Box>
      {buyerCanReceiveOrder && (
        <OrderBuyerReceptionButton />
      )}
      {buyerCanDenyOrderDelivery && (
        <OrderDenyDeliveryButton />
      )}
      {supplierCanConfirmPayment && (
        <OrderSupplierConfirmPaymentButton />
      )}
      {supplierCanContestPayment && (
        <OrderSupplierDenyPaymentButton action={TaskAction.Reject} />
      )}
    </Card>
  );
}
